// components/private/order/KonfirmPicView.tsx

import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Users,
    Building2,
    Banknote,
    AlertCircle,
    ChevronLeft,
    ChevronRight,
    Wallet,
    Search,
    RefreshCcw,
    ArrowUpDown,
    ArrowUp,
    ArrowDown,
    Filter,
    Loader2,
    ListFilter // Icon baru untuk limit
} from "lucide-react";
import { axiosClient } from "@/lib/axiosClient";
import toast from "react-hot-toast";
import type { PicApiResponse, ParticipantData, SummaryData, MetaData } from "@/types/pic";

interface KonfirmPicViewProps {
    activePeriodId: number;
    picName: string;
}

// Helper Format Rupiah
const formatRupiah = (number: number) => {
    return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(number);
};

export const KonfirmPicView = ({ activePeriodId, picName }: KonfirmPicViewProps) => {
    // --- State Data ---
    const [participants, setParticipants] = useState<ParticipantData[]>([]);
    const [summary, setSummary] = useState<SummaryData | null>(null);
    const [meta, setMeta] = useState<MetaData | null>(null);

    // --- State UI & Params ---
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState("10"); // 🔹 State baru untuk Limit
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState<string>("all");
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
        key: 'created_at',
        direction: 'desc'
    });

    // --- Fetch Data Function ---
    const fetchGroupData = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axiosClient.get<PicApiResponse>(`/pic/participants`, {
                params: {
                    period_id: activePeriodId,
                    page: page,
                    per_page: perPage,               // 🔹 Kirim param per_page
                    search: search,
                    status: statusFilter === "all" ? undefined : statusFilter,
                    sort_by: sortConfig.key,
                    sort_order: sortConfig.direction
                }
            });

            setParticipants(data.data);
            setSummary(data.summary);
            setMeta(data.meta);
        } catch (error) {
            console.error("Error fetching group:", error);
            toast.error("Gagal memuat data grup.");
        } finally {
            setLoading(false);
        }
    }, [activePeriodId, page, perPage, search, statusFilter, sortConfig]); // 🔹 Tambah perPage ke dependency

    // --- Effects ---

    // Debounce Search
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchGroupData();
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [fetchGroupData]);

    // --- Handlers ---

    const handleSort = (key: string) => {
        setSortConfig((current) => ({
            key,
            direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const handleReload = () => {
        fetchGroupData();
        toast.success("Data diperbarui");
    };

    const handleNextPage = () => {
        if (meta && page < meta.last_page) setPage((prev) => prev + 1);
    };

    const handlePrevPage = () => {
        if (page > 1) setPage((prev) => prev - 1);
    };

    const renderSortIcon = (columnKey: string) => {
        if (sortConfig.key !== columnKey) return <ArrowUpDown className="ml-2 h-4 w-4 opacity-50" />;
        return sortConfig.direction === 'asc'
            ? <ArrowUp className="ml-2 h-4 w-4 text-primary" />
            : <ArrowDown className="ml-2 h-4 w-4 text-primary" />;
    };

    // --- Render Loading Awal ---
    if (loading && !summary && page === 1 && !search) {
        return (
            <div className="space-y-4 animate-pulse">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[1, 2, 3].map((i) => <div key={i} className="h-24 bg-gray-200 dark:bg-gray-700 rounded-xl" />)}
                </div>
                <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-full" />
                <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded-xl" />
            </div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="space-y-6"
        >
            {/* 1. Header & Summary Cards (Tidak berubah) */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-100 dark:border-blue-800">
                    <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                        <CardTitle className="text-sm font-medium text-blue-700 dark:text-blue-300">Total Anggota</CardTitle>
                        <Users className="w-4 h-4 text-blue-500" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                            {summary?.total_participants ?? 0}
                        </div>
                        <p className="text-xs text-blue-600/80 dark:text-blue-400 mt-1">Orang terdaftar</p>
                    </CardContent>
                </Card>
                <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                    <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                        <CardTitle className="text-sm font-medium text-gray-500">Total Tagihan</CardTitle>
                        <Wallet className="w-4 h-4 text-gray-500" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold text-gray-900 dark:text-white">
                            {formatRupiah(summary?.total_biaya ?? 0)}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Akumulasi seluruh paket</p>
                    </CardContent>
                </Card>
                <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                    <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                        <CardTitle className="text-sm font-medium text-gray-500">Status Pembayaran</CardTitle>
                        <Banknote className="w-4 h-4 text-gray-500" />
                    </CardHeader>
                    <CardContent>
                        <div className="flex justify-between items-end">
                            <div>
                                <span className="text-xs text-gray-400">Belum</span>
                                <div className="text-xl font-bold text-red-600 dark:text-red-400">{summary?.total_unpaid ?? 0}</div>
                            </div>
                            <div className="text-right">
                                <span className="text-xs text-gray-400">Lunas</span>
                                <div className="text-xl font-bold text-green-600 dark:text-green-400">{summary?.total_paid ?? 0}</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* 2. Controls: Search, Filter, Limit, Reload */}
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                <div className="relative w-full md:w-72">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                    <Input
                        placeholder="Cari nama, email..."
                        className="pl-9"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(1);
                        }}
                    />
                </div>
                <div className="flex gap-2 w-full md:w-auto flex-wrap justify-end">

                    {/* 🔹 SELECT LIMIT PER HALAMAN */}
                    <Select
                        value={perPage}
                        onValueChange={(val) => {
                            setPerPage(val);
                            setPage(1); // Reset ke halaman 1 jika limit berubah
                        }}
                    >
                        <SelectTrigger className="w-[180px]">
                            <ListFilter className="w-4 h-4 mr-2" />
                            <SelectValue placeholder="Limit" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="10">10 Baris</SelectItem>
                            <SelectItem value="25">25 Baris</SelectItem>
                            <SelectItem value="50">50 Baris</SelectItem>
                            <SelectItem value="100">100 Baris</SelectItem>
                        </SelectContent>
                    </Select>

                    <Select
                        value={statusFilter}
                        onValueChange={(val) => {
                            setStatusFilter(val);
                            setPage(1);
                        }}
                    >
                        <SelectTrigger className="w-[230px]">
                            <Filter className="w-4 h-4 mr-2" />
                            <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Semua Status</SelectItem>
                            <SelectItem value="paid">Lunas</SelectItem>
                            <SelectItem value="unpaid">Belum Lunas</SelectItem>
                        </SelectContent>
                    </Select>

                    <Button variant="outline" size="icon" onClick={handleReload} title="Muat Ulang" disabled={loading}>
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />}
                    </Button>
                </div>
            </div>

            {/* 3. Table Data */}
            <Card className="overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm rounded-2xl bg-white dark:bg-gray-800">
                <CardHeader className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex flex-row items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <Building2 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                        <div>
                            <CardTitle className="text-lg font-semibold text-gray-800 dark:text-white">Daftar Anggota Tim</CardTitle>
                            <CardDescription>PIC: {picName}</CardDescription>
                        </div>
                    </div>
                    {summary?.participants_per_package && (
                        <div className="hidden md:flex gap-2">
                            {summary.participants_per_package.map((pkg) => (
                                <Badge key={pkg.package_id} variant="secondary" className="text-[10px]">
                                    {pkg.package_name}: {pkg.total}
                                </Badge>
                            ))}
                        </div>
                    )}
                </CardHeader>

                <CardContent className="p-0">
                    <div className="relative w-full overflow-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="hover:bg-transparent">
                                    <TableHead className="w-[50px] text-center">No</TableHead>
                                    <TableHead className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" onClick={() => handleSort('full_name')}>
                                        <div className="flex items-center">Nama Peserta {renderSortIcon('full_name')}</div>
                                    </TableHead>
                                    <TableHead className="hidden md:table-cell">Instansi</TableHead>
                                    <TableHead className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" onClick={() => handleSort('package_id')}>
                                        <div className="flex items-center">Paket {renderSortIcon('package_id')}</div>
                                    </TableHead>
                                    <TableHead>Harga</TableHead>
                                    <TableHead className="text-right">Status</TableHead>
                                </TableRow>
                            </TableHeader>

                            <TableBody>
                                {loading ? (
                                    // 🔹 SKELETON LOADING PREMIUM
                                    [...Array(parseInt(perPage) > 5 ? 5 : parseInt(perPage))].map((_, i) => (
                                        <TableRow key={`skeleton-${i}`} className="hover:bg-transparent">
                                            {/* 1. No: Kotak kecil rounded */}
                                            <TableCell className="text-center">
                                                <div className="mx-auto h-6 w-6 rounded-md bg-slate-200 dark:bg-slate-800 animate-pulse" />
                                            </TableCell>

                                            {/* 2. Nama & No Reg: Dua baris, baris atas lebih panjang & gelap */}
                                            <TableCell>
                                                <div className="flex flex-col gap-2">
                                                    <div className="h-4 w-32 sm:w-48 rounded bg-slate-300 dark:bg-slate-700 animate-pulse" />
                                                    <div className="h-3 w-24 rounded bg-slate-200 dark:bg-slate-800 animate-pulse" />
                                                </div>
                                            </TableCell>

                                            {/* 3. Instansi: Baris panjang acak lebar */}
                                            <TableCell className="hidden md:table-cell">
                                                <div className="h-4 w-[80%] rounded bg-slate-200 dark:bg-slate-800 animate-pulse" />
                                            </TableCell>

                                            {/* 4. Paket: Bentuk Badge (Pill Shape) */}
                                            <TableCell>
                                                <div className="h-6 w-20 rounded-full bg-slate-200 dark:bg-slate-800 animate-pulse" />
                                            </TableCell>

                                            {/* 5. Harga: Angka monospace look */}
                                            <TableCell>
                                                <div className="h-4 w-24 rounded bg-slate-200 dark:bg-slate-800 animate-pulse" />
                                            </TableCell>

                                            {/* 6. Status: Badge di kanan (Pill Shape) */}
                                            <TableCell className="text-right">
                                                <div className="ml-auto h-6 w-16 rounded-full bg-slate-200 dark:bg-slate-800 animate-pulse" />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : participants.length > 0 ? (
                                    // 🔹 DATA ASLI (Tidak Berubah)
                                    participants.map((p, index) => {
                                        const rowNumber = (meta ? (meta.current_page - 1) * meta.per_page : 0) + index + 1;
                                        return (
                                            <TableRow key={p.participant_id}>
                                                <TableCell className="text-center font-medium text-gray-500">{rowNumber}</TableCell>
                                                <TableCell>
                                                    <div className="font-medium text-gray-900 dark:text-gray-100">{p.full_name}</div>
                                                    <div className="text-xs text-gray-500">{p.registration.registration_number}</div>
                                                </TableCell>
                                                <TableCell className="hidden md:table-cell text-gray-600 dark:text-gray-400">{p.institution}</TableCell>
                                                <TableCell>
                                                    <Badge variant="outline" className="font-normal">{p.registration.package_name}</Badge>
                                                </TableCell>
                                                <TableCell className="font-mono text-xs">{formatRupiah(p.registration.price)}</TableCell>
                                                <TableCell className="text-right">
                                                    <Badge className={`uppercase text-[10px] ${p.registration.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`} variant="secondary">
                                                        {p.registration.payment_status}
                                                    </Badge>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    // 🔹 EMPTY STATE (Tidak Berubah)
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center py-10 text-gray-500">
                                            <div className="flex flex-col items-center justify-center gap-2 animate-in fade-in zoom-in-95 duration-300">
                                                <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-full">
                                                    <AlertCircle className="w-6 h-6 opacity-40" />
                                                </div>
                                                <p className="font-medium">Tidak ditemukan data peserta.</p>
                                                {search && <p className="text-xs text-muted-foreground">Coba ubah kata kunci pencarian "{search}".</p>}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>

                {/* 4. Pagination */}
                {meta && meta.last_page > 1 && (
                    <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800">
                        <div className="text-xs text-gray-500">
                            Halaman {meta.current_page} dari {meta.last_page} ({meta.total} Data)
                        </div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={handlePrevPage} disabled={page === 1 || loading} className="h-8 w-8 p-0">
                                <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={handleNextPage} disabled={page === meta.last_page || loading} className="h-8 w-8 p-0">
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                )}
            </Card>
        </motion.div>
    );
};